/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { makeStyles } from '@mui/styles';
import { Modal } from 'apollo-react';
import { AlignLeft, Search } from 'apollo-react-icons';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { batch, useDispatch } from 'react-redux';

import NewComponent from './components/NewComponent';
import NewComponentOptions from './components/NewComponentOptions';
import TextBoxPopover from './components/TextBoxPopover';
import EntityModel from './PropertyComponentModal/ExpressionModal/EntityModel';
import { MODEL_PICKER_MODAL_STYLES } from '../../../constants/CommonStylesConstants';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getUITA } from '../../../utils/commonUtils';
import {
  addNewTextViewToFormModel,
  addNewZoomToFormModel,
  getIsAdvancedTextBoxValid,
  moveControlByDndToEmptyContainer,
  moveControlByDndWithinContainer,
  setSelectedControl,
  updateMovedFieldsFormModelAfterMove,
  updateUserActionArray,
} from '../../actions/editor.actions';
import { MOVE_CONTROL_ACTION } from '../../constants/editor.constants';
import { CONTAINER_CONTROLS, SS_CONTROL_TYPE } from '../../constants/form-manager.constants';
import {
  getDeltaChanges,
  getFormInfo,
  getLayoutChanges,
  hasVisibleWarning,
} from '../../selectors/common.selectors';
import {
  getAddedElementFormModelById,
  getClosestParentIdByType,
  getShowControlWrapper,
  hasControlDeltaChanges,
  isSelectedControl,
} from '../../selectors/editor.selectors';
import { getModelById } from '../../selectors/form-manager.selectors';
import { acceptedDropTypes } from '../../utils/editor.utils';

const useStyles = makeStyles({
  root: {
    cursor: 'pointer',
    borderRadius: 5,
    border: 'solid 1px transparent',
    borderStyle: 'dashed',
    padding: '4px',
    position: 'relative',
    '& .Mui-disabled:not(.MuiIconButton)': {
      opacity: '1 !important',
    },
    '& input.Mui-disabled': {
      backgroundColor: '#FFFFFF !important',
    },
    '& input.Mui-disabled + fieldset': {
      backgroundColor: '#FFFFFF !important',
      borderColor: 'rgba(230, 230, 230, 0.48) !important',
    },
    '& div[class*="MuiSelect-select"].Mui-disabled': {
      backgroundColor: '#FFFFFF !important',
      color: '#000000 !important',
    },
    '& button[class*="MuiButton-outlinedPrimary"].Mui-disabled': {
      color: '#0768fd !important',
    },
    '&:hover *': {
      cursor: 'pointer',
    },
    '& *': {
      marginTop: '0 !important',
      marginBottom: '0 !important',
    },
    '& label': {
      color: '#000000 !important',
    },
  },
  tableWrapper: {
    width: '100%',
  },
  hover: {
    border: 'dashed 1px #ccc',
  },
  selected: {
    borderColor: '#6c6969',
    background: '#f6f7fb',
    '&:hover': {
      borderColor: '#6c6969',
    },
    '& div[class*="MuiPaper-root"]': {
      backgroundColor: '#f6f7fb',
    },
    '& .Mui-disabled circle:nth-child(2), rect': {
      fill: '#ffffff !important',
    },
  },
  selectedGridHeader: {
    background: '#ffffff',
  },
  tooltipItem: {
    width: '100%',
    opacity: 0,
    marginBottom: '-10px !important',
  },
  propChanged: {
    borderColor: 'blue',
    '&:hover': {
      borderColor: 'blue',
    },
  },

  hasWarning: {
    border: '2px solid red !important',
  },

  newElement: { display: 'flex', justifyContent: 'flex-end' },
  AddedFields: {
    borderColor: 'green !important',
  },
  newElementAddedFromFormModel: {
    border: '1px dashed orange !important',
    '&:hover': {
      border: '1px dashed orange !important',
    },
  },
  hasUIChanges: {
    borderColor: 'orange !important',
    '&:hover': {
      borderColor: 'orange !important',
    },
  },
  ssTextBox: {
    maxWidth: ({ inTable }) => (inTable ? '100%' : 450),
    flex: ({ inTable }) => (inTable ? '1 1 100%' : 1),
  },
  ssTextView: {
    width: 'fit-content',
    minWidth: 165,
    display: 'flex',
    alignItems: 'center',
    flex: ({ inTable }) => (inTable ? '1 1 100%' : 0),
  },
  ssZoom: ({ isSelected, hasControlWrapper }) =>
    isSelected && hasControlWrapper
      ? {
          display: 'flex',
          alignItems: 'center',
        }
      : {
          width: 65,
          paddingTop: 22,
        },
  ssHyperLink: {
    display: 'flex',
    alignItems: 'center',
  },
  ssIcon: {
    width: ({ isSelected, hasControlWrapper }) => (isSelected && hasControlWrapper ? 90 : 65),
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: ({ isSelected, hasControlWrapper }) =>
      isSelected && hasControlWrapper ? 'flex-start' : 'center',
  },
  ssSingleTab: {
    '& > div:first-child': {
      height: '100%',
    },
  },
  title: {
    fontSize: 18,
    padding: '10px 0 10px 0',
    fontWeight: 'bold',
    borderBottom: 'solid 3px #eee',
  },
  canDrop: {
    borderColor: '#0f0 !important',
    backgroundColor: '#cbffcb !important',
    transition: 'all .2s',
  },
  isDragging: {
    '& *': {
      visibility: 'hidden !important',
    },
  },
  entityModelModal: MODEL_PICKER_MODAL_STYLES,
  iconWrapper: {
    width: '100%',
  },
});

const TITLE_COMPONENTS = [SS_CONTROL_TYPE.FORM];

const lookupItems = [
  {
    icon: <Search style={{ fontSize: 15, marginRight: 10 }} />,
    text: 'Zoom',
    value: SS_CONTROL_TYPE.ZOOM,
  },
  {
    icon: <AlignLeft style={{ fontSize: 15, marginRight: 10 }} />,
    text: 'Text View',
    value: SS_CONTROL_TYPE.TEXT_VIEW,
  },
];

const ControlWrapper = ({ children, controlType, id, inTable }) => {
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [showDataModelPicker, setShowDataModelPicker] = useState(false);
  const [fieldValue, setFieldValue] = useState(null);

  const hasWarning = useFormSelector(hasVisibleWarning(id));

  const [hoverClass, setHoverClass] = useState('');
  const isSelected = useFormSelector(isSelectedControl, { id });
  const hasControlWrapper = useFormSelector(getShowControlWrapper, { id });
  const classes = useStyles({ isSelected, hasControlWrapper, inTable });

  const component = useFormSelector(getModelById, { id });
  const newElement = useFormSelector(getAddedElementFormModelById, { controlId: id });

  const { parent } = component ?? {};

  const parentComponent = useFormSelector(getModelById, {
    id: parent ?? '',
  });

  const formInfo = useFormSelector(getFormInfo);
  const { formKey } = formInfo || {};
  const title = TITLE_COMPONENTS.includes(controlType) && parent === 'root' && formKey;

  const deltaChanges = useFormSelector(getDeltaChanges);
  const LayoutChanges = useFormSelector(getLayoutChanges);
  const { AddedFields } = LayoutChanges || [];

  const isAddedField = React.useMemo(
    () => AddedFields && AddedFields.find((item) => item.controlId === id),
    [id, AddedFields]
  );

  const hasControlChanges = useFormSelector(hasControlDeltaChanges, { id });
  const hasUIChanges = newElement !== undefined || hasControlChanges;

  const controls = deltaChanges?.controls;
  const propChanges = controls
    ? controls.filter((item) =>
        item.ControlId.includes('formBox_') ? id.includes(item.ControlId) : item.ControlId === id
      )
    : [];

  const handleOnClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (e.target.closest('[data-prevent-select]')) {
        return;
      }

      if (hasControlWrapper && isSelected) {
        dispatch(setSelectedControl(id, false));
      } else {
        dispatch(setSelectedControl(id, true));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSelected, hasControlWrapper]
  );

  const isSSTextBox = controlType === SS_CONTROL_TYPE.TEXT_BOX;
  const isSSTextView = controlType === SS_CONTROL_TYPE.TEXT_VIEW;
  const isSSZoom = controlType === SS_CONTROL_TYPE.ZOOM;
  const isSSHyperLink = controlType === SS_CONTROL_TYPE.HYPER_LINK;
  const isSSGridColumn = controlType === SS_CONTROL_TYPE.GRID_COLUMN;
  const isSSSubGrid = controlType === SS_CONTROL_TYPE.SUB_GRID;
  const isSSSingleTab = controlType === SS_CONTROL_TYPE.SINGLE_TAB;
  const isSSIcon = controlType === SS_CONTROL_TYPE.ICON;

  const [dragIconPressed, setDragIconPressed] = useState(false);
  const closestFormId =
    useFormSelector(getClosestParentIdByType, {
      id,
      type: SS_CONTROL_TYPE.FORM,
    }) ?? 'form-prefix';
  const closestGridId =
    useFormSelector(getClosestParentIdByType, {
      id,
      type: SS_CONTROL_TYPE.GRID,
    }) ?? 'grid-prefix';

  const isChildrenNodesEmpty = () =>
    CONTAINER_CONTROLS.includes(controlType) && !component.childrenNodes.length;

  // TODO: uncomment when have ability to move controls between different containers
  // const dropPrefixes = [closestFormId];
  const dropPrefixes = [parentComponent?.controlId];

  if (controlType === SS_CONTROL_TYPE.GRID_COLUMN && !isChildrenNodesEmpty()) {
    dropPrefixes.push(closestGridId);
  }

  const canDragControl = useCallback(
    () =>
      controlType !== SS_CONTROL_TYPE.FORM &&
      controlType !== SS_CONTROL_TYPE.SUB_GRID &&
      parentComponent?.controlType !== SS_CONTROL_TYPE.ADVANCED_TEXT_BOX,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentComponent]
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: `${dropPrefixes.join('')}--${controlType}`,
      item: { dragId: id, dragParentComponent: parentComponent },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => isSelected && hasControlWrapper && dragIconPressed && canDragControl(),
      end: () => {
        setDragIconPressed(false);
      },
    }),
    [isSelected, hasControlWrapper, dragIconPressed, canDragControl]
  );

  const [{ isOverCurrent }, drop] = useDrop(
    () => ({
      accept: isChildrenNodesEmpty()
        ? acceptedDropTypes(controlType, closestFormId)
        : acceptedDropTypes(parentComponent?.controlType, dropPrefixes.join('')),
      drop: (item, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        const dragId = item.dragId;
        const dropId = id;
        const dragParentComponent = item.dragParentComponent;
        const dropParentComponent = parentComponent;

        if (dragId !== dropId) {
          if (isChildrenNodesEmpty()) {
            const dropComponent = component;

            batch(() => {
              dispatch(
                moveControlByDndToEmptyContainer(dragId, dragParentComponent, dropComponent)
              );
              dispatch(updateMovedFieldsFormModelAfterMove(dropComponent.controlId, dragId));
            });
          } else if (dragParentComponent.controlId === dropParentComponent.controlId) {
            const dragIdIndex = parentComponent.childrenNodes.indexOf(dragId);
            const dropIdIndex = parentComponent.childrenNodes.indexOf(dropId);

            if (dragIdIndex === dropIdIndex + 1) {
              return;
            }

            const movedControlParams = {
              dragId,
              dropId,
              parentComponent,
              moveType: 'DND',
            };

            batch(() => {
              dispatch(moveControlByDndWithinContainer(dragId, dropId, parentComponent));
              dispatch(updateMovedFieldsFormModelAfterMove(parentComponent.controlId, dragId));
              dispatch(
                updateUserActionArray({
                  actionType: MOVE_CONTROL_ACTION,
                  userChanges: movedControlParams,
                })
              );
            });
          } else {
            // TODO: uncomment when have ability to move controls between different containers
            return;
            // batch(() => {
            //   dispatch(
            //     moveControlByDndToAnotherContainer(
            //       dragId,
            //       dropId,
            //       dragParentComponent,
            //       dropParentComponent
            //     )
            //   );
            //   dispatch(updateMovedFieldsFormModelAfterMove(dropParentComponent.controlId, dragId));
            // });
          }
        }
      },
      collect: (monitor) => ({
        isOverCurrent: monitor.isOver({ shallow: true }),
      }),
    }),
    [id, component, parentComponent]
  );

  const handleValueSelect = (value) => {
    setPopoverOpen(false);

    const isAdvancedTextBoxValid = dispatch(
      getIsAdvancedTextBoxValid({ controlId: id, newControlType: value, translateString })
    );

    if (!isAdvancedTextBoxValid) {
      return;
    }

    if (value === SS_CONTROL_TYPE.ZOOM) {
      dispatch(addNewZoomToFormModel(component));
    } else if (value === SS_CONTROL_TYPE.TEXT_VIEW) {
      setShowDataModelPicker(true);
    }
  };

  const onUpdateValue = (value) => {
    dispatch(addNewTextViewToFormModel(component, value));
    handleCloseDialog();
  };

  const onSelectValue = (value) => {
    setFieldValue(value);
  };

  const onPopoverClose = () => {
    setPopoverOpen(false);
  };

  const handleCloseDialog = () => {
    setShowDataModelPicker(false);
  };

  const showTextBoxOptions = (e) => {
    setPopoverAnchor(e.currentTarget);
    setPopoverOpen(true);
  };

  return (
    <>
      <div
        className={classNames({
          [classes.root]: true,
          [classes.tableWrapper]: controlType === SS_CONTROL_TYPE.GRID,
          [classes.hover]: !!hoverClass,
          [classes.selected]: hasControlWrapper && isSelected && !isDragging,
          [classes.selectedGridHeader]:
            hasControlWrapper &&
            isSelected &&
            !isDragging &&
            component?.controlType === SS_CONTROL_TYPE.GRID_COLUMN,
          [classes.propChanged]: !isSelected && propChanges && propChanges.length,
          [classes.newElementAddedFromFormModel]: newElement && !hasWarning,
          [classes.AddedFields]: isAddedField && !hasWarning,
          [classes.hasUIChanges]: hasUIChanges && !hasWarning,
          [classes.ssTextBox]: isSSTextBox,
          [classes.ssTextView]: isSSTextView,
          [classes.ssZoom]: isSSZoom,
          [classes.ssHyperLink]: isSSHyperLink,
          [classes.ssIcon]: isSSIcon,
          [classes.canDrop]: isOverCurrent,
          [classes.isDragging]: isDragging,
          [classes.hasWarning]: hasWarning,
          [classes.ssSingleTab]: isSSSingleTab,
        })}
        ref={drop}
        onClick={handleOnClick}
        id={id}
        onMouseEnter={(e) => {
          e.stopPropagation();
          if (!isDragging) {
            setHoverClass(classes.hover);
          }
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          setHoverClass('');
        }}
      >
        <div ref={drag} className={isSSIcon && classes.iconWrapper}>
          {title && <div className={classes.title}>{title}</div>}
          <NewComponentOptions
            component={component}
            isSelected={isSelected}
            hasControlWrapper={hasControlWrapper}
            setDragIconPressed={setDragIconPressed}
            canDragControl={canDragControl}
            showTextBoxOptions={showTextBoxOptions}
          >
            {children}
          </NewComponentOptions>
        </div>
      </div>
      {parent !== 'root' &&
        !isSSGridColumn &&
        !isSSSubGrid &&
        !isSSSingleTab &&
        parentComponent.controlType !== SS_CONTROL_TYPE.ADVANCED_TEXT_BOX && (
          <div>
            <NewComponent
              controlParentId={parentComponent && parentComponent.controlId}
              previousControlId={id}
              inTable={inTable}
            />
          </div>
        )}
      {parentComponent?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX &&
        isSSTextBox &&
        popoverOpen && (
          <TextBoxPopover
            open={popoverOpen}
            anchorEl={popoverAnchor}
            onClose={onPopoverClose}
            items={lookupItems}
            onSelect={handleValueSelect}
          />
        )}
      {parentComponent?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX &&
        isSSTextBox &&
        showDataModelPicker && (
          <Modal
            title={translateString('Entity Model')}
            className={classes.entityModelModal}
            open={showDataModelPicker}
            onClose={handleCloseDialog}
            onClick={(e) => {
              e.stopPropagation();
            }}
            buttonProps={[
              {
                label: translateString('Cancel'),
                onClick: handleCloseDialog,
                'ui-ta': getUITA('Entity Model', 'Cancel Btn'),
              },
              {
                label: translateString('Save'),
                onClick: () => {
                  onUpdateValue(fieldValue);
                  handleCloseDialog();
                },
                'ui-ta': getUITA('Entity Modesl', 'Save Btn'),
              },
            ]}
            disableBackdropClick
            disableEscapeKeyDown
          >
            <EntityModel
              action={true}
              select={onSelectValue}
              change={onUpdateValue}
              translation={false}
              showSearch
              showTitle={false}
            />
          </Modal>
        )}
    </>
  );
};

export default React.memo(ControlWrapper);
